import { router } from "@inertiajs/react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { stateRewardPath } from "@/routes"

/**
 * OKS-407 — Inline State control for the Brand Rewards index row
 * (replaces the OKS-59 `RewardToggle` Switch; TRD KD-3).
 *
 * The brand admin picks one of the three reward states
 * (available / coming_soon / locked) directly on the catalogue row.
 *
 * Posture (UX Spec §4 micro-interactions):
 *   - Optimistic update: set the local value first so the row reacts
 *     immediately.
 *   - Server reconcile via `router.patch(stateRewardPath(id), { state })`
 *     with `preserveScroll` + `preserveState` so the table doesn't
 *     re-mount and scroll stays put. The controller's flash fires a
 *     sonner toast.
 *   - Revert on server-side failure (`onError`).
 *
 * Defence-in-depth: the Select is `disabled` mid-flight to prevent
 * double-submits.
 */
export const REWARD_STATES = ["available", "coming_soon", "locked"] as const
export type RewardState = (typeof REWARD_STATES)[number]

interface RewardStateSelectProps {
  rewardId: number
  rewardTitle: string
  state: RewardState
}

export function RewardStateSelect({
  rewardId,
  rewardTitle,
  state,
}: RewardStateSelectProps) {
  const { t } = useTranslation()
  const [value, setValue] = useState<RewardState>(state)
  const [pending, setPending] = useState(false)

  function handleChange(next: string) {
    const nextState = next as RewardState
    const previous = value

    setValue(nextState)
    setPending(true)

    router.patch(
      stateRewardPath(rewardId),
      { state: nextState },
      {
        preserveScroll: true,
        preserveState: true,
        onError: () => {
          // Revert on server validation failure; the flash toast
          // surfaces `rewards.flash.state_change_failed`.
          setValue(previous)
        },
        onFinish: () => {
          setPending(false)
        },
      },
    )
  }

  return (
    <Select
      items={REWARD_STATES.map((option) => ({
        value: option,
        label: t(`rewards.state.${option}`),
      }))}
      value={value}
      disabled={pending}
      onValueChange={(next) => next != null && handleChange(next)}
    >
      <SelectTrigger
        size="sm"
        aria-label={t("rewards.state_select.aria_label", {
          title: rewardTitle,
        })}
        data-testid="reward-state-select"
        data-reward-id={rewardId}
        className="w-[150px]"
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {REWARD_STATES.map((option) => (
          <SelectItem key={option} value={option}>
            {t(`rewards.state.${option}`)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
